/* eslint-disable max-lines */
import { action, computed, IObservableArray, observable } from 'mobx';
import { ExperimentStore } from 'bernie-plugin-mobx';

import { ILocalization } from 'bernie-l10n';

import { convertRoomChildrenToString } from 'src/common/utils/room-utils';

export interface RoomConfig {
  adults: number;
  children: number[];
}

export interface Travelers {
  adults?: string;
  children?: string;
}

export interface TravelerCount {
  guests: number;
  rooms: number;
}

export class RoomPickerModel {
  private initAdults: string;
  private initChildren: string;
  private initialRoomCount: number;
  private l10n: ILocalization;
  private experiment?: ExperimentStore;
  private isPackageShoppingPath: boolean;

  /* room data structure:
    {
      adults: Number,
      children: [
        Number, Number ... // each number represents AGE
      ]
    }
  */
  @observable
  public rooms: IObservableArray<RoomConfig> = [] as IObservableArray<RoomConfig>;

  @observable
  public petsIncluded: boolean;

  constructor(
    { adults, children }: Travelers = {},
    l10n: ILocalization,
    experiment?: ExperimentStore,
    isPackageShoppingPath: boolean = false,
    petsIncluded = false
  ) {
    this.l10n = l10n;
    this.experiment = experiment;
    this.isPackageShoppingPath = isPackageShoppingPath;
    this.petsIncluded = petsIncluded;
    this.setupRooms(adults, children);
  }

  private static DISPLAY_SUMMARY_KEY = 'hotels.roomPicker.roomSummary';
  private static VRBEX_DISPLAY_SUMMARY_KEY = 'hotels.roomPicker.roomSummary.singleUnit';
  private static VRBEX_DISPLAY_SUMMARY_KEY_WITH_PETS = 'hotels.roomPicker.roomSummary.singleUnit.withPets';

  private static DEFAULT_SA_CHILD_AGE: number = 0;
  private static DEFAULT_MULTI_ITEM_CHILD_AGE: number = 2;
  private static MAX_ROOM_COUNT: number = 8;
  static MAX_GUESTS: number = 14;

  get initialProps(): Travelers {
    return {
      adults: this.initAdults,
      children: this.initChildren,
    };
  }

  @computed
  get finalProps(): Travelers {
    const adults = this.rooms
      .map(room => room.adults)
      .filter(adult => adult > 0)
      .join(',');
    const children = this.rooms
      .map((room, index) => convertRoomChildrenToString(index + 1, room.children))
      .filter(child => child !== '')
      .join(',');

    return { adults, children };
  }

  @computed
  get counts(): TravelerCount {
    return this.rooms.reduce(
      (obj: TravelerCount, room: RoomConfig) => {
        obj.guests += room.adults + room.children.length;

        return obj;
      },
      { guests: 0, rooms: this.rooms.length }
    );
  }

  @computed
  get displaySummary(): string {
    const { rooms, guests } = this.counts;
    const adjustedGuests = guests > RoomPickerModel.MAX_GUESTS ? RoomPickerModel.MAX_GUESTS : guests;

    return this.l10n.formatText(this.displaySummaryKey, rooms, adjustedGuests);
  }

  @computed
  get displaySummaryKey(): string {
    if (this.experiment?.exposures?.Vrbex_generic?.bucket === 1) {
      return this.petsIncluded ? RoomPickerModel.VRBEX_DISPLAY_SUMMARY_KEY_WITH_PETS : RoomPickerModel.VRBEX_DISPLAY_SUMMARY_KEY;
    }

    return RoomPickerModel.DISPLAY_SUMMARY_KEY;
  }

  @computed
  get roomCount(): number {
    return this.rooms.length;
  }

  @computed
  get disableAddRoom(): boolean {
    if (this.isPackageShoppingPath && this.counts.guests >= 6) {
      return true;
    }

    if (this.rooms.length < this.initialRoomCount) {
      return false;
    }

    return this.rooms.length >= RoomPickerModel.MAX_ROOM_COUNT || this.counts.guests >= RoomPickerModel.MAX_GUESTS;
  }

  @computed
  get enableInfantSeatSelection(): boolean {
    return this.rooms.some(room => room.children.some(childAge => childAge <= 1));
  }

  @action
  public includePets(): void {
    this.petsIncluded = true;
  }

  @action
  public excludePets(): void {
    this.petsIncluded = false;
  }

  @action
  public addRoom(): void {
    this.rooms.push({ adults: 1, children: [] });
  }

  @action
  public removeRoom(room: RoomConfig): void {
    this.rooms = this.rooms.filter(rm => rm !== room) as IObservableArray<RoomConfig>;
  }

  @action
  public changeAdultsForRoom(room, count): void {
    const temp: number = room.adults;
    room.adults = count;
    if (this.counts.guests > RoomPickerModel.MAX_GUESTS) {
      room.adults = temp;
    }
  }

  @action
  public changeChildrenForRoom(room, count, isInfant = false): void {
    let diff = this.infantAwareDiff(room, count, isInfant);
    if (diff > 0) {
      for (diff; diff > 0; diff--) {
        const type =
          isInfant || !this.isPackageShoppingPath ? RoomPickerModel.DEFAULT_SA_CHILD_AGE : RoomPickerModel.DEFAULT_MULTI_ITEM_CHILD_AGE;
        room.children.push(type);
      }
    } else if (diff < 0) {
      this.infantAwareSplice(room, diff, isInfant);
    }
    if (this.counts.guests > RoomPickerModel.MAX_GUESTS) {
      room.children.pop();
    }
  }

  @action
  public changeChildAge(roomIndex: number, childIndex: number, age: number, isInfant = false): void {
    this.rooms = observable(
      this.rooms.map((observableRoom, roomI) => {
        if (childIndex > -1 && childIndex < observableRoom.children.length && roomI === roomIndex) {
          let trackedIndex = -1;
          for (let index = 0; index < observableRoom.children.length; index++) {
            const value = observableRoom.children[index];
            if ((isInfant && value < 2) || (!isInfant && value >= 2) || !this.isPackageShoppingPath) {
              // eslint-disable-next-line no-plusplus
              trackedIndex++;
            }

            if (trackedIndex === childIndex) {
              observableRoom.children[index] = age;

              break;
            }
          }
        }

        return observableRoom;
      })
    );
  }

  @action
  public reset(): void {
    this.setupRooms(this.initAdults, this.initChildren);
  }

  // Only used by MultiItem
  public static determineMaxGuests = (currentSectionGuests: number, totalGuests: number): number => currentSectionGuests + 6 - totalGuests;

  // private helpers

  public setupRooms(adultsText: string, childrenText: string) {
    const adults: string[] = adultsText && adultsText.length > 0 ? adultsText.split(',') : [];
    const childrenRooms: number[][] = this.convertChildrenTextToRooms(childrenText);
    const numberOfRooms: number = Math.max(adults.length, childrenRooms.length);
    const rooms: IObservableArray<RoomConfig> = [] as IObservableArray<RoomConfig>;

    for (let i = 0; i < numberOfRooms; i++) {
      // note: parseInt() can return NaN
      const roomAdults: number = i < adults.length ? parseInt(adults[i], 10) : 0;
      const roomChildren: number[] = childrenRooms[i] ? childrenRooms[i] : [];

      rooms.push({
        adults: isNaN(roomAdults) ? 0 : roomAdults,
        children: roomChildren,
      });
    }

    this.initAdults = adultsText;
    this.initChildren = childrenText;
    this.rooms.replace(rooms);
    this.initialRoomCount = rooms.length;
  }

  private infantAwareDiff(room, count, isInfant): number {
    if (this.isPackageShoppingPath) {
      const ageGroup = room.children.filter(age => (isInfant ? age < 2 : age >= 2)).length;

      return count - ageGroup;
    }

    return count - room.children.length;
  }

  private infantAwareSplice(room, diff, isInfant): void {
    if (this.isPackageShoppingPath) {
      // find the last of type and remove it until our diff is 0.
      for (let i = room.children.length - 1; i > -1; i--) {
        const age = room.children[i];
        if ((age < 2 && isInfant) || (age >= 2 && !isInfant)) {
          room.children.splice(i, 1);
          // eslint-disable-next-line no-plusplus
          diff++;
        }
        if (diff === 0) {
          break;
        }
      }
    } else {
      room.children.splice(room.children.length + diff, -diff);
    }
  }

  private convertChildrenTextToRooms(childrenText: string): number[][] {
    const items: string[] = childrenText && childrenText.length > 0 ? childrenText.split(',') : [];

    return items.reduce((rooms: number[][], item) => {
      const roomAge: string[] = item.split('_');
      if (roomAge.length !== 2) {
        return rooms; // invalid format sent down
      }

      let roomNumber: number = parseInt(roomAge[0], 10);
      const age: number = parseInt(roomAge[1], 10);
      if (isNaN(roomNumber) || isNaN(age)) {
        return rooms; // invalid data type for room_age
      }
      roomNumber -= 1; // turn to 0-indexed for rooms array

      if (rooms[roomNumber]) {
        rooms[roomNumber].push(age);
      } else {
        rooms[roomNumber] = [age];
      }

      return rooms;
    }, []);
  }
}
